import React from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Copy } from '@styled-icons/fa-solid/Copy';
import { toast } from 'react-toastify';
import { getSiteName } from '../../hooks/useSiteConfig';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle,
} from './shared/ModalBase';

const BanAppealSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 107, 0.2);
`;

const AppealTitle = styled.h3`
  color: var(--clr-text);
  margin: 0 0 1rem 0;
  text-align: center;
  font-size: 1.1rem;
`;

const AppealDescription = styled.p`
  color: var(--clr-text-secondary);
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ContactMethod = styled.div`
  background-color: var(--clr-prim);
  border: 1px solid color-mix(in srgb, var(--clr-sec) 30%, transparent);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin: 0.5rem 0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--clr-text);
  font-weight: 500;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-1px);
    background-color: var(--clr-hover);
  }
`;

const CopyIcon = styled(Copy)`
  width: 0.9rem;
  height: 0.9rem;
  color: var(--clr-text);
`;

const DiscordButton = styled.div`
  background-color: var(--clr-prim);
  border: 1px solid color-mix(in srgb, var(--clr-sec) 30%, transparent);
  border-radius: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  color: var(--clr-text);
  font-weight: 500;
  font-size: 0.9rem;
  margin: 0.5rem 0;

  &:hover {
    transform: translateY(-1px);
    background-color: var(--clr-hover);
  }

  a {
    color: var(--clr-text);
    text-decoration: none;
    display: block;
    margin: -0.75rem;
    padding: 0.75rem;
  }
`;

function BannedModal({ visible, onClose, banDetails }) {
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied!`);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <Modal
      isOpen={!!visible}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000 },
        content: {
          border: 'none',
          background: 'transparent',
          padding: 0,
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <ModalWrapper>
        <ModalContent>
          <ModernTitle>You can't send messages</ModernTitle>
          <ModernSubtitle>
            You're currently banned on {getSiteName()}.
          </ModernSubtitle>

          <BanAppealSection>
            <AppealTitle>You are currently banned</AppealTitle>

            {banDetails?.banId != null && (
              <AppealDescription
                style={{
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                }}
              >
                Ban ID: #{banDetails.banId}
              </AppealDescription>
            )}

            {banDetails?.unbanDate && (
              <AppealDescription style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Your ban will be lifted on:{' '}
                {new Date(banDetails.unbanDate).toLocaleDateString()} at{' '}
                {new Date(banDetails.unbanDate).toLocaleTimeString()}
              </AppealDescription>
            )}

            {banDetails?.externalReason && (
              <AppealDescription
                style={{
                  fontWeight: 600,
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 107, 107, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 107, 107, 0.3)',
                }}
              >
                Reason: {banDetails.externalReason}
              </AppealDescription>
            )}

            {banDetails?.reason && !banDetails?.externalReason && (
              <AppealDescription style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Reason: {banDetails.reason}
              </AppealDescription>
            )}

            {banDetails?.bannedMessage && (
              <AppealDescription
                style={{
                  fontWeight: 600,
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 107, 107, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 107, 107, 0.3)',
                }}
              >
                Banned Message: "{banDetails.bannedMessage}"
              </AppealDescription>
            )}

            <AppealDescription>
              If you believe this ban was issued in error or would like to
              appeal, please join our Discord server and post in #ban-appeals
              {banDetails?.banId != null ? ` with your ban ID (#${banDetails.banId})` : ''}
              {' '}so all moderators can review your case.
            </AppealDescription>

            <DiscordButton
              style={{
                marginBottom: '0.5rem',
                backgroundColor: '#5865F2',
                borderColor: '#5865F2',
              }}
            >
              <a
                href='https://discord.gg/rM7g5Zusmr'
                target='_blank'
                rel='noreferrer noopener'
                style={{ color: 'white', fontWeight: '600' }}
              >
                Appeal by joining the {getSiteName()} Discord
              </a>
            </DiscordButton>

            <AppealDescription style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
              Or email us for ban appeals (responses may be slower):
            </AppealDescription>
            <ContactMethod onClick={() => copyToClipboard('officialmodsofvs@gmail.com', 'Email')}>
              <CopyIcon />
              officialmodsofvs@gmail.com
            </ContactMethod>

            <AppealDescription style={{ marginTop: '1.5rem' }}>
              You can still use the Private Rage Room — it's 100% offline and stays on your device.
            </AppealDescription>
            <DiscordButton
              style={{
                marginBottom: '0.5rem',
                backgroundColor: '#8b1e1e',
                borderColor: '#8b1e1e',
              }}
            >
              <a
                href='/rage'
                target='_blank'
                rel='noreferrer noopener'
                style={{ color: 'white', fontWeight: '600' }}
              >
                Open Private Rage Room
              </a>
            </DiscordButton>
          </BanAppealSection>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
}

export default BannedModal;
